import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { emptySplitApi } from "./empty-split-api";
import type {
  AnalyseResponse,
  OpportunityAppointmentsResponse,
  PlaybookResponse,
  SalesReview,
  SalesReviewStatus,
  SalesReviewsResponse
} from "./types/sales-coach.type";

/**
 * `/coach/opportunity-appointments/` has `default_limit = 5` and
 * `/coach/sales-reviews/` has 25, so EVERY read here passes an explicit limit —
 * a five-row page looks like a bug in our code, not like a default.
 *
 * `max_limit` is 300 and 100 respectively. The review list's 100 is the ceiling on
 * the training deck's sample, and it also caps the opportunities merge below.
 */
export const OPPORTUNITIES_PAGE_SIZE = 25;
export const SALES_REVIEWS_MAX_LIMIT = 100;
export const SALES_REVIEWS_DECK_LIMIT = SALES_REVIEWS_MAX_LIMIT;

/** The two orderings the API actually supports on each list. Nulls sort last both ways. */
export type OpportunityOrdering =
  | "start_moment"
  | "-start_moment"
  | "review_score"
  | "-review_score";

export type SalesReviewOrdering =
  | "visit_date"
  | "-visit_date"
  | "composite_score"
  | "-composite_score";

/**
 * Shared filter shape. Everything here is a SERVER parameter — nothing on these
 * lists is filtered or sorted in the browser, because both are server-paginated
 * and a client predicate over one page of 25 leaves the `count` in the footer
 * describing a different set entirely.
 *
 * Dates are naive `yyyy-MM-dd` strings, interpreted in the PRACTICE timezone
 * (`CustomDateTimeFilter` on a `TimezoneAwareFilterSet`). Build them with
 * `features/sales-coach/utils/sales-coach.datetime.ts` — never `toISOString()`.
 */
export interface SalesCoachListFilters {
  /** `>=`, naive ⇒ practice timezone. */
  after_date?: string;
  /** `<` — EXCLUSIVE. For an inclusive end day send the next day. */
  before_date?: string;
  provider?: number[];
  appointment_type?: number[];
  outcome?: string[];
  review_state?: string[];
  min_score?: number;
  max_score?: number;
}

export interface OpportunityAppointmentsQueryArgs
  extends SalesCoachListFilters {
  limit?: number;
  offset?: number;
  ordering?: OpportunityOrdering;
  /**
   * Comma-separated appointment ids — the key the appointment page resolves ONE row by.
   *
   * Mirrors the `appointment` filter that already exists on `/coach/sales-reviews/`.
   * BACKEND-FIRST: django-filter silently ignores an unknown query param, so against an API
   * that predates it the whole filter is dropped and an UNFILTERED first page comes back —
   * i.e. some other patient's appointment. `useSalesCoachReviewAccess` detects that from the
   * envelope's `count` and discards it; see `docs/ambient-scribes/sales-coach/SN-19586-BACKEND-REQUIREMENTS.md`.
   */
  appointment?: number[];
  /** Comma-separated ambient-scribe ids. Same backend addition, same caveat. */
  scribe?: number[];
}

export interface SalesReviewsQueryArgs extends SalesCoachListFilters {
  limit?: number;
  offset?: number;
  ordering?: SalesReviewOrdering;
  /** The merge key for the opportunities page. Comma-separated appointment ids. */
  appointment?: number[];
  /** Defaults to the current catalog server-side. An unknown version yields an EMPTY PAGE, not a 404. */
  framework_version?: string;
}

/**
 * Named rather than an inline intersection, and the URLs are built by exported
 * functions rather than inline in `query` — this repo sits near TypeScript's
 * relation-cache ceiling (`services/follow-ups.ts` documents the `RangeError`),
 * and the failure mode of a query string is a well-formed URL that quietly means
 * something else, which no runtime assertion would catch.
 */
/**
 * `base` is the route prefix — `/coach` for v1, `/coach/v2` for the v2 twins in
 * `services/sales-coach-v2.ts`. A second positional rather than a key in `args`,
 * because every key in `args` is serialized into the query string.
 */
export function buildOpportunitiesUrl(
  {
    limit = OPPORTUNITIES_PAGE_SIZE,
    offset = 0,
    ordering = "-start_moment",
    ...filters
  }: OpportunityAppointmentsQueryArgs,
  base = "/coach"
): string {
  const qs = formatValidQueryParamUrlCommaSeparated({
    limit,
    offset,
    ordering,
    ...filters
  });
  return `${base}/opportunity-appointments/?${qs}`;
}

export function buildSalesReviewsUrl(
  {
    limit = SALES_REVIEWS_DECK_LIMIT,
    offset = 0,
    ordering = "-visit_date",
    ...filters
  }: SalesReviewsQueryArgs,
  base = "/coach"
): string {
  const qs = formatValidQueryParamUrlCommaSeparated({
    limit: Math.min(limit, SALES_REVIEWS_MAX_LIMIT),
    offset,
    ordering,
    ...filters
  });
  return `${base}/sales-reviews/?${qs}`;
}

/**
 * No `query=` projection on ANY of these reads.
 *
 * A RestQL inclusion list REPLACES the default payload and naming a field the
 * serializer lacks 400s the entire read with a bare-array body. Every field on
 * `SalesReviewListSerializer` is a `SerializerMethodField` and `DynamicFieldsMixin`
 * projecting those has not been exercised on that route — the contract's own advice
 * is to verify against a real server first. A 100-row deck page is ~1.2 MB, which is
 * cheaper than a silent breakage. See the curl in the plan's verification section.
 */
export const salesCoachApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getOpportunityAppointments: builder.query<
      OpportunityAppointmentsResponse,
      OpportunityAppointmentsQueryArgs
    >({
      query: (args) => buildOpportunitiesUrl(args),
      providesTags: [{ type: "SalesCoachOpportunities" as const, id: "LIST" }]
    }),

    /**
     * Start — or re-use — the scored review for one appointment. No request body.
     *
     * NEVER auto-retried: `baseQueryWithReauthFn`'s `retry()` wrapper is GET-only
     * and excludes mutations, which is deliberate here because a retry risks a
     * second paid model run. The backend is idempotent anyway — the unique
     * constraint on (appointment, framework_version) is the lock, so double-posting
     * returns the same `review_id` and enqueues nothing.
     *
     * A 404 has two deliberately indistinguishable causes: another practice's row,
     * or a row that is no longer eligible. Treat it as "this row is gone — refresh".
     */
    analyseOpportunity: builder.mutation<AnalyseResponse, number>({
      query: (appointmentId) => ({
        url: `/coach/opportunity-appointments/${appointmentId}/analyse/`,
        method: "POST",
        timeout: 30_000
      }),
      invalidatesTags: [
        { type: "SalesCoachOpportunities" as const, id: "LIST" }
      ]
    }),

    /**
     * The poll, and the ONLY healer: a worker killed at the hard time limit dies by
     * SIGKILL and writes nothing, so without this read the row sits at `running`
     * forever. The list deliberately does not sweep — that would be write
     * amplification on a read route.
     *
     * Untagged and `keepUnusedDataFor: 0`: invalidating a list on every 3s tick
     * would refetch the whole page continuously, and a cached status would report a
     * finished run as still running.
     */
    getSalesReviewStatus: builder.query<SalesReviewStatus, number>({
      query: (reviewId) => `/coach/sales-reviews/${reviewId}/status/`,
      keepUnusedDataFor: 0
    }),

    getSalesReview: builder.query<SalesReview, number>({
      query: (reviewId) => `/coach/sales-reviews/${reviewId}/`,
      providesTags: (result, error, reviewId) => [
        { type: "SalesCoachReviews" as const, id: reviewId }
      ]
    }),

    /**
     * 204, empty body. A POST rather than a side effect of the GET: a write inside
     * `retrieve` would land on a route the router sends to the read replica and
     * would fire on every refresh. Idempotent — conditional on `read_at IS NULL`,
     * so the recorded moment is the first open.
     *
     * Invalidates BOTH lists: `unread` is derived on each of them.
     */
    markSalesReviewRead: builder.mutation<void, number>({
      query: (reviewId) => ({
        url: `/coach/sales-reviews/${reviewId}/mark-read/`,
        method: "POST"
      }),
      invalidatesTags: (result, error, reviewId) => [
        { type: "SalesCoachOpportunities" as const, id: "LIST" },
        { type: "SalesCoachReviews" as const, id: "LIST" },
        { type: "SalesCoachReviews" as const, id: reviewId }
      ]
    }),

    getSalesReviews: builder.query<SalesReviewsResponse, SalesReviewsQueryArgs>(
      {
        query: (args) => buildSalesReviewsUrl(args),
        providesTags: [{ type: "SalesCoachReviews" as const, id: "LIST" }]
      }
    ),

    /**
     * The play catalog — the only thing that turns `D2.companion_direct_ask` into
     * "Ask the companion directly". Not practice data; the same catalog for every
     * practice, and a published version is immutable and `lru_cache`d server-side,
     * so this is held for the session rather than tagged.
     *
     * An UNKNOWN version 404s here (unlike the review list, which returns an empty
     * page) — here the version IS the resource.
     */
    getSalesPlaybook: builder.query<PlaybookResponse, string | undefined>({
      query: (frameworkVersion) =>
        frameworkVersion
          ? `/coach/playbook/?framework_version=${encodeURIComponent(frameworkVersion)}`
          : "/coach/playbook/",
      keepUnusedDataFor: 3600
    })
  })
});

export const {
  useGetOpportunityAppointmentsQuery,
  useAnalyseOpportunityMutation,
  useGetSalesReviewStatusQuery,
  useLazyGetSalesReviewStatusQuery,
  useGetSalesReviewQuery,
  useMarkSalesReviewReadMutation,
  useGetSalesReviewsQuery,
  useGetSalesPlaybookQuery
} = salesCoachApi;
